import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DataTable, { Column } from '../components/DataTable'
import Modal from '../components/Modal'
import {
  LocalUserCreate,
  LocalUserUpdate,
  UnifiedUser,
  localUsersApi,
} from '../services/api'

interface RoleOption {
  value: string
  label: string
  description: string
}

const ROLE_OPTIONS: RoleOption[] = [
  { value: 'system_admin', label: 'System Admin', description: 'Full platform access' },
  { value: 'rehapiano_admin', label: 'RehaPiano Admin', description: 'Platform administration' },
  { value: 'institution_admin', label: 'Institution Admin', description: 'Institution-level operations' },
  { value: 'doctor', label: 'Doctor', description: 'Clinical role with restricted admin features' },
  { value: 'app_admin', label: 'App Admin', description: 'App approvals and app-team management' },
  { value: 'app_dev', label: 'App Developer', description: 'App development workflow access' },
]

const ROLE_LABELS: Record<string, string> = ROLE_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label
  return acc
}, {} as Record<string, string>)

const toggleRole = (roles: string[], role: string, checked: boolean): string[] => {
  if (checked) {
    return roles.includes(role) ? roles : [...roles, role]
  }
  return roles.filter((item) => item !== role)
}

const UserProviderBadge: React.FC<{ provider: UnifiedUser['auth_provider'] }> = ({ provider }) => {
  if (provider === 'local') {
    return <span className="badge bg-emerald-100 text-emerald-800">Local</span>
  }
  if (provider === 'clinic') {
    return <span className="badge bg-teal-100 text-teal-800">Clinic Portal</span>
  }
  return <span className="badge bg-blue-100 text-blue-800">Keycloak</span>
}

const LocalUsersPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [providerFilter, setProviderFilter] = useState<'all' | 'local' | 'keycloak' | 'clinic'>('all')

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedLocalUser, setSelectedLocalUser] = useState<UnifiedUser | null>(null)

  const [createForm, setCreateForm] = useState({
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    email: '',
    roles: ['system_admin'],
    is_active: true,
  })

  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    roles: [] as string[],
    is_active: true,
    must_change_password: false,
  })

  const [passwordForm, setPasswordForm] = useState({
    new_password: '',
    confirm_password: '',
    must_change_password: true,
  })

  const { data, isLoading } = useQuery({
    queryKey: ['combined-users', providerFilter],
    queryFn: () =>
      localUsersApi.listCombined({
        auth_provider: providerFilter === 'all' ? undefined : providerFilter,
      }),
  })
  const { data: totalUsersData, isLoading: isLoadingTotalUsers } = useQuery({
    queryKey: ['combined-users-total'],
    queryFn: () => localUsersApi.listCombined(),
  })

  const users = data?.data || []
  const totalUsersCount = totalUsersData?.data?.length ?? 0

  const refreshUsers = async () => {
    await queryClient.invalidateQueries({ queryKey: ['combined-users'] })
    await queryClient.invalidateQueries({ queryKey: ['combined-users-total'] })
    await queryClient.invalidateQueries({ queryKey: ['local-users'] })
  }

  const createMutation = useMutation({
    mutationFn: async (payload: LocalUserCreate) => localUsersApi.create(payload),
    onSuccess: async () => {
      setError(null)
      setSuccess('Local user created successfully.')
      setIsCreateModalOpen(false)
      setCreateForm({
        username: '',
        password: '',
        first_name: '',
        last_name: '',
        email: '',
        roles: ['system_admin'],
        is_active: true,
      })
      await refreshUsers()
    },
    onError: (err: any) => {
      setSuccess(null)
      setError(err?.response?.data?.detail || 'Failed to create local user')
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (params: { id: string; payload: LocalUserUpdate }) =>
      localUsersApi.update(params.id, params.payload),
    onSuccess: async () => {
      setError(null)
      setSuccess('User details saved successfully.')
      await refreshUsers()
    },
    onError: (err: any) => {
      setSuccess(null)
      setError(err?.response?.data?.detail || 'Failed to update local user')
    },
  })

  const resetMutation = useMutation({
    mutationFn: async (params: { id: string; newPassword: string; mustChangePassword: boolean }) =>
      localUsersApi.resetPassword(params.id, {
        new_password: params.newPassword,
        must_change_password: params.mustChangePassword,
      }),
    onSuccess: async () => {
      setError(null)
      setSuccess('Password updated successfully.')
      setPasswordForm({
        new_password: '',
        confirm_password: '',
        must_change_password: true,
      })
      await refreshUsers()
    },
    onError: (err: any) => {
      setSuccess(null)
      setError(err?.response?.data?.detail || 'Failed to update password')
    },
  })

  const openEditModal = (user: UnifiedUser) => {
    if (user.auth_provider !== 'local') {
      return
    }

    setError(null)
    setSuccess(null)
    setSelectedLocalUser(user)
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      roles: user.roles,
      is_active: user.is_active,
      must_change_password: user.must_change_password,
    })
    setPasswordForm({
      new_password: '',
      confirm_password: '',
      must_change_password: true,
    })
    setIsEditModalOpen(true)
  }

  const submitCreate = (e: React.FormEvent) => {
    e.preventDefault()
    setSuccess(null)
    if (createForm.roles.length === 0) {
      setError('Select at least one role for the new user')
      return
    }
    createMutation.mutate({
      ...createForm,
      email: createForm.email || undefined,
      first_name: createForm.first_name || undefined,
      last_name: createForm.last_name || undefined,
    })
  }

  const submitEdit = (e: React.FormEvent) => {
    e.preventDefault()
    setSuccess(null)
    if (!selectedLocalUser) {
      return
    }
    if (editForm.roles.length === 0) {
      setError('Select at least one role')
      return
    }

    updateMutation.mutate({
      id: selectedLocalUser.id,
      payload: {
        first_name: editForm.first_name || undefined,
        last_name: editForm.last_name || undefined,
        email: editForm.email || undefined,
        roles: editForm.roles,
        is_active: editForm.is_active,
        must_change_password: editForm.must_change_password,
      },
    })
  }

  const submitPasswordChange = () => {
    setSuccess(null)
    if (!selectedLocalUser) {
      return
    }
    if (passwordForm.new_password.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setError('Password confirmation does not match')
      return
    }

    resetMutation.mutate({
      id: selectedLocalUser.id,
      newPassword: passwordForm.new_password,
      mustChangePassword: passwordForm.must_change_password,
    })
  }

  const renderRoleSelector = (
    selectedRoles: string[],
    onToggle: (role: string, checked: boolean) => void,
    testIdPrefix: string,
  ) => (
    <div className="rounded-lg border border-gray-200 p-3 space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Access Roles (same as Keycloak)
      </p>
      <div className="grid grid-cols-1 gap-2">
        {ROLE_OPTIONS.map((role) => (
          <label key={role.value} className="flex items-start gap-2 text-sm text-gray-700">
            <input
              data-testid={`${testIdPrefix}-${role.value}`}
              type="checkbox"
              checked={selectedRoles.includes(role.value)}
              onChange={(e) => onToggle(role.value, e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">{role.label}</span>
              <span className="block text-xs text-gray-500">{role.description}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  )

  const columns: Column<UnifiedUser>[] = useMemo(
    () => [
      {
        key: 'username',
        header: 'Username',
        render: (user) => (
          <div>
            <div className="font-medium">{user.username}</div>
            <div className="text-xs text-gray-500">{user.email || '-'}</div>
          </div>
        ),
        searchable: true,
        // #27: without a searchFn/accessor DataTable skipped this column, so
        // neither username nor e-mail was searchable.
        searchFn: (user, query) =>
          user.username.toLowerCase().includes(query) ||
          (user.email || '').toLowerCase().includes(query),
      },
      {
        key: 'name',
        header: 'Name',
        render: (user) => `${user.first_name || ''} ${user.last_name || ''}`.trim() || '-',
      },
      {
        key: 'provider',
        header: 'Provider',
        render: (user) => <UserProviderBadge provider={user.auth_provider} />,
        sortable: true,
        sortFn: (a, b) => {
          const order: Record<UnifiedUser['auth_provider'], number> = { local: 0, clinic: 1, keycloak: 2 }
          return order[a.auth_provider] - order[b.auth_provider]
        },
      },
      {
        key: 'roles',
        header: 'Roles',
        render: (user) => (
          <div className="flex flex-wrap gap-1">
            {user.roles.map((role) => (
              <span key={`${user.id}-${role}`} className="badge bg-blue-100 text-blue-800" title={role}>
                {ROLE_LABELS[role] || role}
              </span>
            ))}
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (user) =>
          user.is_active ? (
            <span className="badge badge-active">Active</span>
          ) : (
            <span className="badge badge-inactive">Inactive</span>
          ),
      },
      {
        key: 'actions',
        header: 'Actions',
        render: (user) =>
          user.auth_provider === 'local' ? (
            <div className="flex gap-2">
              <button className="btn-secondary px-2 py-1 text-xs" onClick={() => openEditModal(user)}>
                Edit
              </button>
              <button
                className="btn-secondary px-2 py-1 text-xs"
                onClick={() =>
                  updateMutation.mutate({
                    id: user.id,
                    payload: { is_active: !user.is_active },
                  })
                }
              >
                {user.is_active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          ) : user.auth_provider === 'clinic' ? (
            <span className="text-xs text-gray-500">Managed in Clinic Portal</span>
          ) : (
            <span className="text-xs text-gray-500">Managed in Keycloak</span>
          ),
      },
    ],
    [updateMutation]
  )

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Users</h2>
          <p className="text-sm text-gray-600">
            Unified table of local, Keycloak and Clinic Portal users. Local users can be created and
            edited here; Clinic Portal users (doctors) are managed via the Clinic Portal invite flow.
          </p>
          <p className="mt-1 text-sm text-gray-700">
            Total users in system:{' '}
            <span className="font-semibold">{isLoadingTotalUsers ? '...' : totalUsersCount}</span>
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            setError(null)
            setSuccess(null)
            setIsCreateModalOpen(true)
          }}
        >
          Add User
        </button>
      </div>

      {error && !isCreateModalOpen && !isEditModalOpen && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {success && !isCreateModalOpen && !isEditModalOpen && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {success}
        </div>
      )}

      <div className="card">
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm text-gray-600">Provider:</label>
          <select
            className="input w-52"
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value as 'all' | 'local' | 'keycloak' | 'clinic')}
          >
            <option value="all">All users</option>
            <option value="local">Local only</option>
            <option value="clinic">Clinic Portal only</option>
            <option value="keycloak">Keycloak only</option>
          </select>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={users}
        loading={isLoading}
        searchable
        searchPlaceholder="Search users..."
        getRowKey={(user) => user.id}
        emptyMessage="No users found"
      />

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false)
          setError(null)
          setSuccess(null)
        }}
        title="Add Local User"
        size="lg"
      >
        <form className="space-y-3" onSubmit={submitCreate}>
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
              {success}
            </div>
          )}
          <input
            className="input"
            placeholder="Username"
            value={createForm.username}
            onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
            required
          />
          <input
            className="input"
            type="password"
            placeholder="Password"
            value={createForm.password}
            onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
            required
          />
          <input
            className="input"
            placeholder="First name"
            value={createForm.first_name}
            onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
          />
          <input
            className="input"
            placeholder="Last name"
            value={createForm.last_name}
            onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
          />
          <input
            className="input"
            type="email"
            placeholder="Email"
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
          />
          {renderRoleSelector(
            createForm.roles,
            (role, checked) =>
              setCreateForm({
                ...createForm,
                roles: toggleRole(createForm.roles, role, checked),
              }),
            'create-role',
          )}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={createForm.is_active}
              onChange={(e) => setCreateForm({ ...createForm, is_active: e.target.checked })}
            />
            Active
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setIsCreateModalOpen(false)
                setError(null)
                setSuccess(null)
              }}
            >
              Cancel
            </button>
            <button className="btn-primary" type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false)
          setSelectedLocalUser(null)
          setError(null)
          setSuccess(null)
        }}
        title={selectedLocalUser ? `Edit Local User: ${selectedLocalUser.username}` : 'Edit Local User'}
        size="lg"
      >
        {!selectedLocalUser ? null : (
          <div className="space-y-4">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            {success && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                {success}
              </div>
            )}
            <form className="space-y-3" onSubmit={submitEdit}>
              <input
                className="input"
                placeholder="First name"
                value={editForm.first_name}
                onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
              />
              <input
                className="input"
                placeholder="Last name"
                value={editForm.last_name}
                onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
              />
              <input
                className="input"
                type="email"
                placeholder="Email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
              {renderRoleSelector(
                editForm.roles,
                (role, checked) =>
                  setEditForm({
                    ...editForm,
                    roles: toggleRole(editForm.roles, role, checked),
                  }),
                'edit-role',
              )}
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={editForm.is_active}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={editForm.must_change_password}
                  onChange={(e) =>
                    setEditForm({ ...editForm, must_change_password: e.target.checked })
                  }
                />
                Must change password
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button type="submit" className="btn-primary" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>

            <div className="border-t border-gray-200 pt-4 space-y-3">
              <h4 className="text-sm font-semibold text-gray-900">Change Password</h4>
              <input
                className="input"
                type="password"
                placeholder="New password (min 8 characters)"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              />
              <input
                className="input"
                type="password"
                placeholder="Confirm new password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={passwordForm.must_change_password}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      must_change_password: e.target.checked,
                    })
                  }
                />
                Require password change at next login
              </label>
              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={submitPasswordChange}
                  disabled={resetMutation.isPending}
                >
                  {resetMutation.isPending ? 'Updating Password...' : 'Update Password'}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default LocalUsersPage
